import { Button, Card, CardContent, Grid, CardHeader, Typography } from "@mui/material";
import { useState } from "react";
import { apiConstants, getGeneric, patchGeneric } from '../../common/constants'
import * as React from 'react'
import PageTitle from "../../components/PageTitle";
import { useSnackbar } from "notistack";
import Loader from "../../components/Loader";
import { useForm } from "react-hook-form";
import { FormProvider, RHFCheckbox2, RHFSwitch } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import RHFMultipleSelectField from "../../common/hook-form/RHFMultipleSelectField";
import AutoApproveReturn from "./AutoApproveReturn";

function ReturnConfig({ type, title }) {
    const [loading, setLoading] = useState(false)
    const { enqueueSnackbar } = useSnackbar();
    const [reload, setReload] = useState(0)

    const validationSchema = Yup.object().shape({
        merchant: Yup.string().required('Merchant is required'),
        status: Yup.boolean(),
        channels: Yup.array().when('status', (status, schema) => {
            return status
                ? schema.min(1, 'At least one communication type is required')
                : schema.notRequired();
        }),
    });

    const methods = useForm({
        defaultValues: {
            merchant: '',
            status: false,
            channels: '',
            isSmsCostExcluded: false,
            isWaCostExcluded: false,
            isManualReturn: false,
            isAutoApproval: false
        },
        resolver: yupResolver(validationSchema)
    });


    const {
        handleSubmit,
        reset,
        watch,
        setValue
    } = methods;

    const onSubmit = (data) => {
        setLoading(true)

        // Convert channels array to communicationTypes object
        const communicationTypes = {};
        channelsOptions.forEach(option => {
            communicationTypes[option.value] = data.channels.includes(option.value);
        });

        const payload = {
            "isActive": data.status,
            "communicationTypes": communicationTypes,
            "isSmsCostExcluded": data.isSmsCostExcluded,
            "isWaCostExcluded": data.isWaCostExcluded,
            "isManualReturn": data.isManualReturn,
            "isAutoApproval": data.isAutoApproval,
            // "isRefundManagedByProzo": data.isRefundManagedByProzo
        }
        patchGeneric(`${apiConstants.TOGGLE_RETURN_CONFIG}/${data.merchant}`, payload, {
        }).then((res) => {
            enqueueSnackbar("Success", { variant: 'success' });
            setReload(reload + 1)
            // reset();
            setLoading(false)
        }).catch((err) => {
            setLoading(false)
            enqueueSnackbar("Some error occurred", { variant: 'error' });
            console.log(err);
        })
    };

    const channelsOptions = [
        { value: 'SMS', label: 'SMS' },
        { value: 'WHATSAPP', label: 'WHATSAPP' },
    ]
    const merchantValue = watch('merchant')

    const merchantFilter = () => {
        if (merchantValue) {
            setLoading(true)
            getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantValue}`).then((res) => {

                const enabledChannels = [];
                if (res.data?.returnConfig?.communicationTypes) {
                    Object.entries(res.data.returnConfig.communicationTypes).forEach(([type, enabled]) => {
                        if (enabled) {
                            enabledChannels.push(type);
                        }
                    });
                    setValue('channels', enabledChannels);
                    setValue('status', res.data?.returnConfig?.isActive || false)
                    setValue('isSmsCostExcluded', res.data?.returnConfig?.isSmsCostExcluded || false)
                    setValue('isWaCostExcluded', res.data?.returnConfig?.isWaCostExcluded || false)
                    setValue('isManualReturn', res.data?.returnConfig?.isManualReturn || false)
                    setValue('isAutoApproval', res.data?.returnConfig?.isAutoApproval || false)
                    // setValue('isRefundManagedByProzo', res.data?.returnConfig?.isRefundManagedByProzo || false)
                } else {
                    setValue('channels', [])
                    setValue('status', false)
                    setValue('isManualReturn', false)
                    setValue('isAutoApproval', false)
                    // setValue('isRefundManagedByProzo', false)
                }
                setLoading(false)
            }).catch((err) => {
                setLoading(false)
                console.log(err);
            })    // eslint-disable-next-line react-hooks/exhaustive-deps
        }
    }

    React.useEffect(() => {
        if (merchantValue) {
            merchantFilter()
        }
    }, [merchantValue, reload])


    return (
        <>
            <PageTitle>Return & Exchange Configuration</PageTitle>
            {loading && <Loader />}
            <Card sx={{ mb: 2 }}>
                <CardHeader 
                    title="Configure Return Settings"
                    subheader="Set up return policies, communication preferences, and approval workflows for merchants"
                />
                <CardContent>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                                <MerchantFilterCached name="merchant" label="Select Merchant" />
                            </Grid>
                            <Grid item xs={4}>
                                <RHFMultipleSelectField
                                    width="100%"
                                    name="channels"
                                    label={`Communication Type`}
                                    options={channelsOptions}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <RHFSwitch
                                    name="status"
                                    label="Enable Return Configuration"
                                    helperText="Toggle to activate/deactivate return functionality"
                                    color="primary"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <RHFCheckbox2
                                    name='isSmsCostExcluded'
                                    label="Exclude SMS Cost"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 3, mb: 1, display: 'block' }}>
                                    When enabled, SMS charges will not be included in the return cost calculation
                                </Typography>
                                <RHFCheckbox2
                                    name='isWaCostExcluded'
                                    label="Exclude WhatsApp Cost"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 3, mb: 1, display: 'block' }}>
                                    When enabled, WhatsApp charges will not be included in the return cost calculation
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <RHFCheckbox2
                                    name='isManualReturn'
                                    label="Manual Return"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 3, mb: 1, display: 'block' }}>
                                    When enabled, returns will require manual webhook trigger from merchant
                                </Typography>
                                <RHFCheckbox2
                                    name='isAutoApproval'
                                    label="Auto Approval"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 3, mb: 1, display: 'block' }}>
                                    When enabled, returns will be automatically approved for selected return reason without manual intervention
                                </Typography>
                            </Grid>
                            {/* <Grid item xs={4}>
                                <RHFCheckbox2
                                    name='isRefundManagedByProzo'
                                    label="Refund Managed By Prozo"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 3, mb: 1, display: 'block' }}>
                                    When enabled, refunds for customers will be managed by Prozo.
                                </Typography>
                            </Grid> */}
                            <Grid item xs={12}>
                                <LoadingButton
                                    variant="contained"
                                    sx={{ mt: 1 }}
                                    size="small"
                                    type="submit"
                                >
                                    Submit
                                </LoadingButton>
                                <Button
                                    sx={{ ml: 1, mt: 1 }}
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => {
                                        reset({
                                            merchant: '',
                                            status: false,
                                            channels: '',
                                            isSmsCostExcluded: false,
                                            isWaCostExcluded: false,
                                            isManualReturn: false,
                                            isAutoApproval: false
                                        });
                                    }}
                                >
                                    Reset
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>
            <AutoApproveReturn />
        </>
    );
}

export default ReturnConfig;

